import { useState, useEffect } from 'react';
import { Edit, Plus, Star, Trash2 } from 'lucide-react';
import { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    role: '',
    content: '',
    rating: 5,
    source: '',
    source_url: '',
    is_active: true,
    sort_order: 0,
  });

  const fetchReviews = async () => {
    try {
      const res = await getTestimonials(false);
      setReviews(res.data);
    } catch (err) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const resetForm = () => {
    setForm({ name: '', role: '', content: '', rating: 5, source: '', source_url: '', is_active: true, sort_order: 0 });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (editing) {
        await updateTestimonial(editing.id, payload);
        toast.success('Review updated');
      } else {
        await createTestimonial(payload);
        toast.success('Review added');
      }
      setShowModal(false);
      resetForm();
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save review');
    }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      role: r.role || '',
      content: r.content || '',
      rating: r.rating || 5,
      source: r.source || '',
      source_url: r.source_url || '',
      is_active: r.is_active !== false,
      sort_order: r.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this review?')) return;
    try {
      await deleteTestimonial(id);
      toast.success('Review deleted');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to delete review');
    }
  };

  const sourceOptions = ['Airbnb', 'Booking.com', 'Google', 'Direct', 'Other'];

  return (
    <div data-testid="admin-reviews">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-charcoal">Reviews</h2>
          <p className="text-charcoal-muted">Manage reviews shown on the homepage</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-ubud-jungle hover:bg-ubud-jungle-dark" data-testid="reviews-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Review
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-charcoal-muted">Loading...</TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-charcoal-muted">
                  No reviews yet. Add the first real review!
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-charcoal-muted">{r.role}</TableCell>
                  <TableCell>
                    <div className="flex">
                      {[...Array(r.rating || 0)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-temple-gold text-temple-gold" />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.source ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-ubud-jungle/10 text-ubud-jungle">{r.source}</span>
                    ) : <span className="text-charcoal-muted text-xs">—</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {r.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </TableCell>
                  <TableCell>{r.sort_order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(r)} data-testid={`review-edit-${r.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)} data-testid={`review-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Review' : 'Add New Review'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  placeholder="Reviewer name"
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role / Location *</label>
                <Input
                  placeholder="e.g., Guest from Australia"
                  value={form.role}
                  onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Review Content *</label>
              <Textarea
                placeholder="Paste or type the actual review text..."
                value={form.content}
                onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
                rows={4}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Rating</label>
                <Select value={String(form.rating)} onValueChange={(v) => setForm(p => ({ ...p, rating: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map(n => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Source</label>
                <Select value={form.source || 'none'} onValueChange={(v) => setForm(p => ({ ...p, source: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Platform" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {sourceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sort Order</label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Source URL (optional)</label>
              <Input
                placeholder="https://airbnb.com/rooms/..."
                value={form.source_url}
                onChange={(e) => setForm(p => ({ ...p, source_url: e.target.value }))}
              />
              <p className="text-xs text-charcoal-muted mt-1">Adds a clickable link from the source badge.</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="review_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(p => ({ ...p, is_active: !!checked }))}
              />
              <label htmlFor="review_active" className="text-sm">Show on homepage</label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-ubud-jungle hover:bg-ubud-jungle-dark">
                {editing ? 'Update Review' : 'Create Review'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Blog Management

export default ReviewsManagement;
